// @todo enable the following disabled rules see OPENTOK-31136 for more info
/* eslint-disable one-var, prefer-const, no-plusplus, prefer-rest-params, no-cond-assign */
/* eslint-disable no-prototype-builtins, no-shadow, no-restricted-syntax, no-param-reassign */
/* eslint-disable no-void */

const isFunction = require('lodash/isFunction');
const uuid = require('uuid');

const curryCallAsync = require('../curry_call_async.js');
const createGetStatsAdaptor = require('./create_get_stats_adaptor/create_get_stats_adaptor.js');
const logging = require('../../ot/logging.js');
const MediaStream = require('../media_stream');
const RTCIceCandidate = require('../rtc/rtc_ice_candidate.js');
const RTCSessionDescription = require('../rtc/rtc_session_description.js');

// Our RTCPeerConnection shim, it should look like a normal PeerConection
// from the outside, but it actually delegates to our plugin.
//
const createPeerConnection = function (iceServers, options, plugin) {
  const Proto = function PeerConnection() {};
  const peerConnection = new Proto();
  const id = uuid();
  const candidates = [];

  let resolvePeerConn;
  let rejectPeerConn;
  const peerConnPromise = new Promise((resolve, reject) => {
    resolvePeerConn = resolve;
    rejectPeerConn = reject;
  });

  let events,
    streamAdded;

  plugin.refCounter.add(peerConnection);

  events = {
    addstream: [],
    removestream: [],
    icecandidate: [],
    signalingstatechange: [],
    iceconnectionstatechange: [],
  };

  const onAddIceCandidate = function onAddIceCandidate() { /* success */ };

  const canAddCandidate = function canAddCandidate() {
    return peerConnection.localDescription && peerConnection.remoteDescription;
  };

  const onAddIceCandidateFailed = function onAddIceCandidateFailed(err) {
    logging.error('Failed to process candidate');
    logging.error(err);
  };

  const maybeProcessPendingCandidates = function maybeProcessPendingCandidates() {
    if (canAddCandidate()) {
      for (let i = 0; i < candidates.length; ++i) {
        plugin._.addIceCandidate(id, candidates[i], onAddIceCandidate, onAddIceCandidateFailed);
      }
    }
  };

  const triggerEvent = function triggerEvent(/* eventName [, arg1, arg2, ..., argN] */) {
    const args = Array.prototype.slice.call(arguments);
    const eventName = args.shift();

    if (!events.hasOwnProperty(eventName)) {
      logging.error(`PeerConnection does not have an event called: ${eventName}`);
      return;
    }

    events[eventName].forEach((listener) => {
      listener(...args);
    });
  };

  const bindAndDelegateEvents = function bindAndDelegateEvents(events) {
    for (const name in events) {
      if (events.hasOwnProperty(name)) {
        events[name] = curryCallAsync(events[name]);
      }
    }

    plugin._.on(id, events);
  };

  const callAsync = (fn, ...args) => setTimeout(() => fn(...args));

  const addStream = function addStream(streamJson) {
    const stream = MediaStream.fromJson(streamJson, plugin);
    const event = { stream, target: peerConnection };
    if (peerConnection.onaddstream && isFunction(peerConnection.onaddstream)) {
      callAsync(peerConnection.onaddstream, event);
    }

    triggerEvent('addstream', event);
  };

  const removeStream = function removeStream(streamJson) {
    const stream = MediaStream.getOrCreate(streamJson, plugin);
    const event = { stream, target: peerConnection };

    if (peerConnection.onremovestream && isFunction(peerConnection.onremovestream)) {
      callAsync(peerConnection.onremovestream, event);
    }

    triggerEvent('removestream', event);
  };

  const iceCandidate = function iceCandidate(candidateSdp, sdpMid, sdpMLineIndex) {
    const candidate = new RTCIceCandidate({
      candidate: candidateSdp,
      sdpMid,
      sdpMLineIndex,
    });

    const event = { candidate, target: peerConnection };

    if (peerConnection.onicecandidate && isFunction(peerConnection.onicecandidate)) {
      callAsync(peerConnection.onicecandidate, event);
    }

    triggerEvent('icecandidate', event);
  };

  const signalingStateChange = function signalingStateChange(state) {
    peerConnection.signalingState = state;
    const event = { state, target: peerConnection };

    if (peerConnection.onsignalingstatechange &&
            isFunction(peerConnection.onsignalingstatechange)) {
      callAsync(peerConnection.onsignalingstatechange, event);
    }

    triggerEvent('signalingstatechange', event);
  };

  const iceConnectionChange = function iceConnectionChange(state) {
    peerConnection.iceConnectionState = state;
    const event = { state, target: peerConnection };

    if (peerConnection.oniceconnectionstatechange &&
            isFunction(peerConnection.oniceconnectionstatechange)) {
      callAsync(peerConnection.oniceconnectionstatechange, event);
    }

    triggerEvent('iceconnectionstatechange', event);
  };

  peerConnection.createOffer = constraints => peerConnPromise.then(() => {
    logging.debug('createOffer', constraints);

    return new Promise((resolve, reject) => plugin._.createOffer(
      id,
      (type, sdp) => {
        resolve(new RTCSessionDescription({
          type,
          sdp,
        }));
      },
      reject,
      constraints || {}
    ));
  });

  peerConnection.createAnswer = constraints => peerConnPromise.then(() => {
    logging.debug('createAnswer', constraints);

    return new Promise((resolve, reject) => plugin._.createAnswer(
      id,
      (type, sdp) => {
        resolve(new RTCSessionDescription({
          type,
          sdp,
        }));
      },
      reject,
      constraints || {}
    ));
  });

  peerConnection.setLocalDescription = description => peerConnPromise.then(() => {
    logging.debug('setLocalDescription');

    return new Promise((resolve, reject) => plugin._.setLocalDescription(
      id,
      description,
      () => {
        peerConnection.localDescription = description;
        resolve();
        maybeProcessPendingCandidates();
      },
      reject
    ));
  });

  peerConnection.setRemoteDescription = description => peerConnPromise.then(() => {
    logging.debug('setRemoteDescription');

    return new Promise((resolve, reject) => plugin._.setRemoteDescription(
      id,
      description,
      () => {
        peerConnection.remoteDescription = description;
        resolve();
        maybeProcessPendingCandidates();
      },
      reject
    ));
  });

  peerConnection.addIceCandidate = candidate => peerConnPromise.then(() => {
    logging.debug('addIceCandidate');

    if (!canAddCandidate()) {
      candidates.push(candidate);
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => plugin._.addIceCandidate(
      id,
      candidate,
      resolve,
      reject
    ));
  });

  peerConnection.addStream = stream => peerConnPromise.then(() => {
    const constraints = {};
    plugin._.addStream(id, stream, constraints);
  });

  peerConnection.removeStream = stream => peerConnPromise.then(() => {
    plugin._.removeStream(id, stream);
  });

  peerConnection.getRemoteStreams = function () {
    const remoteStreams = plugin._.getRemoteStreams(id);
    if (remoteStreams) {
      return remoteStreams.map(stream => MediaStream.getOrCreate(stream, plugin));
    }
    return [];
  };

  peerConnection.getLocalStreams = function () {
    return plugin._.getLocalStreams(id).map(stream => MediaStream.getOrCreate(stream, plugin));
  };

  peerConnection.getStreamById = function (streamId) {
    return MediaStream.getOrCreate(plugin._.getStreamById(id, streamId), plugin);
  };

  const adaptedGetStats = createGetStatsAdaptor(plugin._, id);

  peerConnection.getStats = mediaStreamTrack => peerConnPromise.then(() => (
    streamAdded.then(() => adaptedGetStats(mediaStreamTrack))
  ));

  peerConnection.close = function () {
    plugin._.destroyPeerConnection(id);
    plugin.refCounter.remove(this);
  };

  peerConnection.destroy = function () {
    peerConnection.close();
  };

  peerConnection.addEventListener = function (event, handler /* [, useCapture] we ignore this */) {
    if (events[event] === void 0) {
      return;
    }

    events[event].push(handler);
  };

  peerConnection.removeEventListener = function (
    event,
    handler /* [, useCapture] we ignore this */
  ) {
    if (events[event] === void 0) {
      return;
    }

    events[event] = events[event].filter(fn => fn !== handler);
  };

  // These should appear to be null, instead of undefined, if no
  // callbacks are assigned. This more closely matches how the native
  // objects appear and allows 'if (pc.onsignalingstatechange)' type
  // feature detection to work.
  peerConnection.onaddstream = null;
  peerConnection.onremovestream = null;
  peerConnection.onicecandidate = null;
  peerConnection.onsignalingstatechange = null;
  peerConnection.oniceconnectionstatechange = null;

  // Both username and credential must exist, otherwise the plugin throws an error
  iceServers.iceServers.forEach((iceServer) => {
    if (!iceServer.username) {
      iceServer.username = '';
    }
    if (!iceServer.credential) {
      iceServer.credential = '';
    }
  });

  plugin._.registerXCallback('sharedWindowClosed', () => {
    // When a screen shared window is closed we stop the corresponding video track
    const localStreams = peerConnection.getLocalStreams();
    if (localStreams && localStreams.length > 0 && localStreams[0].getVideoTracks()) {
      localStreams[0].getVideoTracks().forEach((videoTrack) => {
        videoTrack.stop();
      });
    } else {
      logging.warn('received sharedWindowClosed for a stream with no video tracks');
    }
  });

  plugin._.initPeerConnection(
    id,
    iceServers,
    options,
    () => {
      bindAndDelegateEvents({
        addStream,
        removeStream,
        iceCandidate,
        signalingStateChange,
        iceConnectionChange,
      });

      streamAdded = new Promise((resolve) => {
        if (plugin._.getLocalStreams(id).length > 0) {
          resolve();
        } else {
          peerConnection.addEventListener('addstream', () => {
            setTimeout(() => {
              resolve();
            }, 200);
          });
        }
      });

      resolvePeerConn(peerConnection);
    },
    (error) => {
      rejectPeerConn(error);
    }
  );

  return peerConnPromise;
};

module.exports = createPeerConnection;



// WEBPACK FOOTER //
// ./src/js/otplugin/peer_connection/create_peer_connection.js